import { Injectable, OnDestroy } from "@angular/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { distinctUntilChanged, map, takeUntil } from "rxjs/operators";

/**
 * Cortes de pantalla del proyecto. Deben coincidir con
 * "src/app/shared/styles/app/breakpoints.scss".
 */
export const VIEWPORT_QUERIES = {
  mobile: "(max-width: 767.98px)",
  compact: "(max-width: 1399.98px)"
};

/**
 * Fuente unica de verdad del tamaño de pantalla en TypeScript.
 *
 * Usa "matchMedia" (via BreakpointObserver del CDK), que solo emite cuando se
 * cruza un corte, en vez de escuchar cada evento "resize" de la ventana.
 * Usar solo cuando cambia la estructura del template (*ngIf); para ocultar,
 * mover o redimensionar basta con los mixins de "breakpoints.scss".
 */
@Injectable()
export class ViewportService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private isMobileSubject = new BehaviorSubject<boolean>(false);
  private isCompactSubject = new BehaviorSubject<boolean>(false);

  /** Emite true en telefonos (menos de 768px). */
  readonly isMobile$: Observable<boolean> = this.isMobileSubject.asObservable();
  /** Emite true en telefonos, tablets y laptops pequeñas (menos de 1400px). */
  readonly isCompact$: Observable<boolean> = this.isCompactSubject.asObservable();

  constructor(private breakpointObserver: BreakpointObserver) {
    this.watch(VIEWPORT_QUERIES.mobile, this.isMobileSubject);
    this.watch(VIEWPORT_QUERIES.compact, this.isCompactSubject);
  }

  /**
   * Indica si la pantalla actual es un telefono.
   * @returns true si el ancho es menor a 768px.
   */
  get isMobile(): boolean {
    return this.isMobileSubject.value;
  }

  /**
   * Indica si la pantalla actual es telefono o tablet.
   * @returns true si el ancho es menor a 1400px.
   */
  get isCompact(): boolean {
    return this.isCompactSubject.value;
  }

  /** Libera las suscripciones a matchMedia. */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sincroniza un subject con el resultado de una media query.
   * @param query media query a observar.
   * @param subject subject que recibe el resultado.
   */
  private watch(query: string, subject: BehaviorSubject<boolean>): void {
    subject.next(this.breakpointObserver.isMatched(query));
    this.breakpointObserver
      .observe(query)
      .pipe(
        map((state) => state.matches),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((matches) => subject.next(matches));
  }
}
